import dynamic from 'next/dynamic';
import Head from 'next/head';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { ReactElement, useCallback, useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { ICookiesPopupProps } from '../../components/common/CookiesPopUp';
import AccordionList, {
  IAccordionListItem,
} from '../../components/common/blocks/AccordionList';
import CallToAction, {
  ICallToActionProps,
} from '../../components/common/blocks/CallToAction';
import ReviewsCarousel, {
  IReviewsCarouselProps,
} from '../../components/common/blocks/ReviewsCarousel';
import TextblocksWithIcon, {
  ITextblocksWithIconProps,
} from '../../components/common/blocks/TextblocksWithIcon';
import { ITrustedByProps } from '../../components/common/blocks/TrustedBy';
import { IFooterProps } from '../../components/common/layout/Footer';
import Layout from '../../components/common/layout/Layout';
import { INavBarProps } from '../../components/common/layout/NavBar';
import { IContactProps } from '../../components/contact-us/Contact';
import { HeroCountry, SellingPoints } from '../../components/country';
import { IHeroCountry } from '../../components/country/HeroCountry';
import RelatedServices, {
  IRelatedService,
} from '../../components/country/RelatedServices';
import RequestCallToAction, {
  IRequestCallToActionProps,
} from '../../components/country/RequestCallToAction';
import { ISellingPoints } from '../../components/country/SellingPoints';
import { useAppSelector } from '../../hooks';
import { PageJSONLDSchema } from '../../models/app.models';
import {
  IBusinessKeyValuesComponent,
  ICallToActionComponent,
  IContactMethod,
  IContactUsForm,
  ICountryPage,
  ICountryRelatedService,
  ICurrency,
  ICustomerReview,
  ICustomerReviewsComponent,
  ICustomerReviewsMeta,
  IGenericServiceLanding,
  ILayoutPageProps,
  IMedia,
  IMetaComponent,
  ITextBlockWithIcon,
  ITrustedByComponent,
} from '../../models/strapi.models';
import { fetchStrapi } from '../../services/api.service';
import { getRoutes } from '../../shared/useBuild';
import {
  buildApiKey,
  getAppCurrencies,
  getCommonTranslations,
  getContactMethods,
  isUndefinedOrEmpty,
  populateCookiesPopUpProps,
  populateFooterProps,
  populateNavBarProps,
  scrollToElement,
} from '../../shared/useCommon';
import {
  generateCustomerReviewsSchema,
  generateFaqsSchema,
  generateOrganizationSchema,
  generateProductSchema,
  generateServiceSchema,
  generateWebsiteSchema,
} from '../../shared/useJSONLDSchema';
import {
  addCurrencySymbol,
  applyCurrencyConversionRate,
  flatStrapiCollection,
  flatStrapiObject,
  formatDate,
  generateRandomId,
  getAlternateLinks,
  getCanonicalLink,
  getContentLanguageCode,
  getImageSrcWithFallback,
  getTranslation,
  keepObjectProperties,
  removeObjectProperties,
} from '../../shared/useUtils';
import { IAppPage } from '../../store/slices/appSlice';
import { NextPageWithLayout } from '../_app';

interface IDeclarationOfUseLandingProps extends ILayoutPageProps {
  heroTipsTitle: string;
  heroTipOne: string;
  heroTipTwo: string;
  heroTipThree: string;
  businessKeyValues: IBusinessKeyValuesComponent;
  trustedBy: ITrustedByComponent;
  customerReviews: ICustomerReviewsComponent;
  reviews: ICustomerReview[];
  heroHeader: string;
  heroHeaderHighlightedText?: string | null;
  heroSubTitle: string;
  serviceTitle: string;
  serviceText: string;
  body: string;
  fromLabel: string;
  orderNowLabel: string;
  fromPrice: number;
  formSuccessMessage: string;
  formErrorMessage: string;
  contactMethods: IContactMethod[];
  contactUsForm: IContactUsForm;
  countryIso: string;
  alternativeLinks: { hrefLang: string; href: string }[];
  canonicalLink: string;
  relatedServicesTitle: string;
  relatedServices: IRelatedService[];
  feesIncludedLabel: string;
  faqsTitle?: string;
  faqs?: IAccordionListItem[];
  pageSchema: PageJSONLDSchema;
  featuredServicesCallToActionProps: ICallToActionProps;
  customerReviewsMeta: ICustomerReviewsMeta;
  customerReviewsTopTipStart: string;
  customerReviewsTopTipEnd: string;
  customerReviewsBottomTip: string;
  howItWorks: ITextblocksWithIconProps;
  introductionTitle: string;
  introductionDescription: string;
  introductionImage: IMedia;
}

const DeclarationOfUseLandingPage: NextPageWithLayout<
  IDeclarationOfUseLandingProps
> = (props: IDeclarationOfUseLandingProps) => {
  const router = useRouter();
  const appState = useAppSelector((state) => state.app);
  const globalCurrency: ICurrency | undefined = useAppSelector(
    (state) => state.app.currentCurrency
  );
  const [hydrated, setHydrated] = useState(false);

  const calculateServiceCurrencyAndPrice = useCallback(
    (fromPrice: number) => {
      const calculatedPrice = !!fromPrice
        ? addCurrencySymbol(
            applyCurrencyConversionRate(
              fromPrice,
              globalCurrency?.conversionRate
            ),
            appState.currentCurrency?.isoCode || 'USD',
            appState.currentLocale || 'en'
          )
        : '-';
      return calculatedPrice;
    },
    [
      appState.currentCurrency?.isoCode,
      appState.currentLocale,
      globalCurrency?.conversionRate,
    ]
  );

  const scrollToContactUs = () => {
    const element = document.getElementById('contact-us');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const heroCountryProps: IHeroCountry = {
    title: props.heroHeader,
    titleHighlightedText: props.heroHeaderHighlightedText,
    countryCode: props.countryIso,
    tips: [
      {
        text: `**${props.customerReviewsMeta.average}** (+${props.customerReviewsMeta.numberOfReviews})`,
        iconPath: '/assets/images/icons/StarRounded.svg',
        action: () => {
          scrollToElement('customer-reviews');
        },
      },
      {
        text: props.heroTipOne,
        iconPath: '/assets/images/icons/ClockIcon.svg',
      },
      {
        text: props.heroTipTwo,
        iconPath: '/assets/images/icons/PersonIcon.svg',
      },
      {
        text: props.heroTipThree,
        iconPath: '/assets/images/icons/CoinsIcon.svg',
      },
    ],
  };

  const requestCallToActionProps: IRequestCallToActionProps = {
    text: props.heroSubTitle,
    fromPrice: calculateServiceCurrencyAndPrice(props.fromPrice),
    fromLabel: props.fromLabel,
    feesIncludedLabel: props.feesIncludedLabel,
    button: {
      text: props.orderNowLabel,
      action: scrollToContactUs,
    },
  };

  const businessKeyValueProps: ISellingPoints = {
    title: props.businessKeyValues.title,
    description: props.businessKeyValues.description,
    sellingPointsItems: [
      {
        title: props.businessKeyValues.keyValueOneTitle,
        description: props.businessKeyValues.keyValueOneDescription,
        iconPath: '/assets/images/icons/ExpertAgentIcon.svg',
      },
      {
        title: props.businessKeyValues.keyValueTwoTitle,
        description: props.businessKeyValues.keyValueTwoDescription,
        iconPath: '/assets/images/icons/WorldWideServicesIcon.svg',
      },
      {
        title: props.businessKeyValues.keyValueThreeTitle,
        description: props.businessKeyValues.keyValueThreeDescription,
        iconPath: '/assets/images/icons/NoHiddenChargesIcon.svg',
      },
      {
        title: props.businessKeyValues.keyValueFourTitle,
        description: props.businessKeyValues.keyValueFourDescription,
        iconPath: '/assets/images/icons/FreeConsultIcon.svg',
      },
    ],
  };

  const trustedByProps: ITrustedByProps = {
    title: props.trustedBy.title,
    companies: props.trustedBy.logos.map((x) => ({
      company: 'company',
      image: (
        <Image
          quality={90}
          src={x.url as string}
          fill
          sizes="175px"
          alt={x.alternativeText || `Partner logo ${x.name}`}
          loading="lazy"
        />
      ),
    })),
  };

  const reviewsCarouselProps: IReviewsCarouselProps = {
    averageScore: props.customerReviewsMeta.average,
    totalReviews: props.customerReviewsMeta.numberOfReviews,
    topTipStart: props.customerReviewsTopTipStart,
    topTipEnd: props.customerReviewsTopTipEnd,
    title: props.customerReviews.title,
    description: props.customerReviews.description,
    defaultLinkPath: props.customerReviews.buttonPath,
    bottomTip: props.customerReviewsBottomTip,
    reviews: props.reviews.map((review) => ({
      text: review.content,
      username: review.customerName,
      score: 5,
      date: formatDate(review.date),
      link: review.link,
    })),
  };

  const contactProps: IContactProps = {
    contactMethods: props.contactMethods.map((x) => ({
      type: x.type,
      label: getTranslation(x.labelTranslations, router.locale || 'en')
        .text as string,
      value:
        x.valueTranslations && x.valueTranslations.length > 0
          ? (getTranslation(x.valueTranslations, router.locale || 'en')
              .text as string)
          : undefined,
    })),
    titleTagName: 'h2',
    formLabels: {
      title: props.contactUsForm.title,
      subTitle: props.contactUsForm.subTitle,
      name: props.contactUsForm.nameLabel,
      email: props.contactUsForm.emailLabel,
      message: props.contactUsForm.messageLabel,
      submit: props.contactUsForm.submitLabel,
      submitSuccessMessage: props.formSuccessMessage,
      submitErrorMessage: props.formErrorMessage,
    },
  };

  useEffect(() => {
    setHydrated(true);
  }, []);

  const DynamicTrustedBy = dynamic(
    () => import('../../components/common/blocks/TrustedBy'),
    {
      ssr: false,
    }
  );

  const DynamicContact = dynamic(
    () => import('../../components/contact-us/Contact'),
    {
      ssr: false,
    }
  );

  return (
    <div className="page-container">
      <Head>
        <title>{props.meta.title}</title>
        <meta name="description" content={props.meta.description} />
        <meta name="keywords" content={props.meta.keywords} />
        <link rel="canonical" href={props.canonicalLink} />
        {props.alternativeLinks.map((x, i) => (
          <link key={i} rel="alternate" hrefLang={x.hrefLang} href={x.href} />
        ))}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(props.pageSchema.nodes),
          }}
        />
      </Head>

      <section className="page-content-section">
        <header>
          <div className="page-content-block">
            <HeroCountry {...heroCountryProps} />
          </div>
          <div className="page-content-block">
            <RequestCallToAction {...requestCallToActionProps} />
          </div>
        </header>
      </section>

      <div className="page-content-section background-light">
        <div className="page-content-block">
          <div className="two-columns">
            <div className="min-height-image-container">
              <Image
                src={getImageSrcWithFallback(props.introductionImage, 'large')}
                alt={
                  props.introductionImage.alternativeText ||
                  props.introductionTitle
                }
                fill
                sizes="(max-width: 768px) 100vw, 75vw"
                quality={90}
                style={{ objectFit: 'contain' }}
              />
            </div>
            <div className="paragraphs-lighter">
              <h2>{props.introductionTitle}</h2>
              <ReactMarkdown>{props.introductionDescription}</ReactMarkdown>
            </div>
          </div>
        </div>
      </div>

      <div className="page-content-section">
        <div className="page-content-block">
          <TextblocksWithIcon {...props.howItWorks} />
        </div>
      </div>

      <div className="page-content-section background-light">
        <div className="page-content-block">
          <SellingPoints {...businessKeyValueProps} />
        </div>
      </div>

      <div id="customer-reviews" className="page-content-section">
        <div className="page-content-block">
          {hydrated && <ReviewsCarousel {...reviewsCarouselProps} />}
        </div>
      </div>

      {props.faqs && props.faqs.length > 0 && (
        <div className="page-content-section">
          <div className="page-content-block">
            <AccordionList title={props.faqsTitle || ''} items={props.faqs} />
          </div>
        </div>
      )}

      {props.relatedServices.length > 0 && (
        <div className="page-content-section">
          <div className="page-content-block">
            <RelatedServices
              title={props.relatedServicesTitle}
              services={props.relatedServices}
            />
          </div>
        </div>
      )}

      {/* Dynamic loaded from this point */}

      <div className="page-content-section background-light">
        <div className="page-content-block">
          <DynamicTrustedBy {...trustedByProps} />
        </div>
      </div>

      <div className="page-content-section is-narrower">
        <div className="page-content-block" id="contact-us">
          <DynamicContact {...contactProps} />
        </div>
      </div>

      <div className="page-content-section is-full-width">
        <div className="page-content-block" id="services">
          <CallToAction {...props.featuredServicesCallToActionProps} />
        </div>
      </div>
    </div>
  );
};

DeclarationOfUseLandingPage.getLayout = function getLayout(page: ReactElement) {
  return (
    <Layout
      locale={page.props.locale}
      navBarProps={page.props.navBarProps}
      cookiesPopUpProps={page.props.cookiesPopUpProps}
      footerProps={page.props.footerProps}
      noindex={page.props.meta.noindex}
      contentLanguageCode={getContentLanguageCode(page.props.locale)}>
      {page}
    </Layout>
  );
};

function fallbackPageMeta(
  locale: string,
  meta: IMetaComponent[],
  countryCode: string
) {
  const localizedMeta = getTranslation(meta, locale);
  const fallback = {
    title: localizedMeta.title || generateRandomId(),
    description: localizedMeta.description || generateRandomId(),
    slug: !!localizedMeta.slug
      ? `${localizedMeta.slug}/${countryCode}`
      : generateRandomId(),
    language: locale,
  };
  return fallback;
}

const entityBaseName = 'declaration-of-use';

export async function getStaticPaths(context: any) {
  let paths: any[] = [];
  const landingsUrlParams = new URLSearchParams({
    'populate[0]': 'metas',
    'pagination[start]': '0',
    'pagination[limit]': '-1',
  });

  const landings: IGenericServiceLanding[] = flatStrapiCollection(
    await fetchStrapi<any>(
      `/${entityBaseName}-landings`,
      landingsUrlParams,
      buildApiKey
    )
  );

  for (const locale of context.locales) {
    const localizedPaths = landings.map((item: IGenericServiceLanding) => {
      const localizedMeta = getTranslation(item.metas, locale);
      return {
        params: {
          slug: localizedMeta.slug,
        },
        locale: locale,
      };
    });
    paths = [...paths, ...localizedPaths];
  }

  return { paths, fallback: false };
}

export async function getStaticProps(
  context: any
): Promise<{ props: IDeclarationOfUseLandingProps }> {
  const appRoutes = await getRoutes();

  const getLocalizedAppRoute = (actualPath: string) => {
    return (
      appRoutes?.find(
        (x: IAppPage) =>
          x &&
          x.destination.replace(`/${context.locale}`, '') === actualPath &&
          x.language === context.locale
      )?.source || actualPath
    );
  };

  const paramSlug = context.params.slug;

  const pageUrlParams = new URLSearchParams({
    'populate[0]': 'meta',
    'populate[1]': 'heroTipsTitle',
    'populate[2]': 'heroTipOne',
    'populate[3]': 'heroTipTwo',
    'populate[4]': 'heroTipThree',
    'populate[5]': 'businessKeyValues',
    'populate[6]': 'trustedBy',
    'populate[7]': 'trustedBy.logos',
    'populate[8]': 'customerReviews',
    'populate[9]': 'fromLabel',
    'populate[10]': 'orderNowLabel',
    'populate[11]': 'contactUsForm',
    'populate[12]': 'formSuccessMessage',
    'populate[13]': 'formErrorMessage',
    'populate[14]': 'relatedServicesTitle',
  });

  const landingUrlParams = new URLSearchParams({
    'filters[metas][slug][$eq]': paramSlug,
    'populate[0]': 'country',
    'populate[1]': 'country.searchClassPrices',
    'populate[2]': 'country.applicationClassPrices',
    'populate[3]': 'country.finalFees',
    'populate[4]': 'country.currency',
    'populate[5]': 'country.nameTranslations',
    'populate[6]': 'metas',
    'populate[7]': 'heroHeaders',
    'populate[8]': 'heroHeadersHighlightedText',
    'populate[9]': 'heroSubTitles',
    'populate[10]': 'serviceTitles',
    'populate[11]': 'serviceTexts',
    'populate[12]': 'bodies',
    'populate[13]': 'faqs',
    'populate[14]': 'breadcrumbAncestors',
    'populate[15]': 'faqsTitle',
    'populate[18]': 'contactUsForm',
    'populate[19]': 'howItWorksTitle',
    'populate[20]': 'howItWorks',
    'populate[21]': 'howItWorks.icon',
    'populate[22]': 'introductionTitle',
    'populate[23]': 'introductionDescription',
    'populate[24]': 'introductionImage',
    'populate[25]': 'businessKeyValues',
  });

  const customerReviewsUrlParams = {
    locale: context.locale,
  };

  const countryBasePage = (
    await fetchStrapi<any>(`/country-page`, pageUrlParams, buildApiKey)
  ).data.attributes as ICountryPage;
  const landingsResult = await fetchStrapi<any>(
    `/${entityBaseName}-landings`,
    landingUrlParams,
    buildApiKey
  );
  const landing: IGenericServiceLanding =
    flatStrapiCollection<IGenericServiceLanding>(landingsResult)[0];
  landing.country = flatStrapiObject(landing.country as any);

  // Contact us form with fallback to country base page
  const contactUsForm: IContactUsForm =
    landing.contactUsForm && landing.contactUsForm.length > 0
      ? getTranslation(landing.contactUsForm, context.locale)
      : getTranslation(countryBasePage.contactUsForm, context.locale);

  // business key values with fallback to country base page
  const businessKeyValues: IBusinessKeyValuesComponent =
    landing.businessKeyValues && landing.businessKeyValues.length > 0
      ? getTranslation(landing.businessKeyValues, context.locale)
      : getTranslation(countryBasePage.businessKeyValues, context.locale);

  const reviews = (
    flatStrapiCollection(
      await fetchStrapi<any>(
        `/customer-reviews`,
        customerReviewsUrlParams,
        buildApiKey
      )
    ) as ICustomerReview[]
  ).map((x) => removeObjectProperties(x, ['id', 'createdAt', 'updatedAt']));

  const trustedBy: ITrustedByComponent = getTranslation(
    countryBasePage.trustedBy,
    context.locale
  );
  trustedBy.logos = flatStrapiCollection(trustedBy.logos as any).map((x) =>
    keepObjectProperties<IMedia>(x, ['url', 'alternativeText', 'name'])
  );

  const customerReviews: ICustomerReviewsComponent = getTranslation(
    countryBasePage.customerReviews,
    context.locale
  );

  const contactMethods = (await getContactMethods()).map((x) =>
    removeObjectProperties(x, ['id', 'createdAt', 'updatedAt'])
  );
  const navBarProps: INavBarProps = await populateNavBarProps(context.locale);
  navBarProps.items = removeObjectProperties(navBarProps.items, [
    'id',
    'createdAt',
    'updatedAt',
  ]);

  const cookiesPopUpProps: ICookiesPopupProps = await populateCookiesPopUpProps(
    context.locale
  );
  const footerProps: IFooterProps = removeObjectProperties(
    await populateFooterProps(context.locale),
    ['id', 'createdAt', 'updatedAt']
  );

  // trademarkLanding → clean up
  const pageMeta =
    getTranslation(landing.metas, context.locale) ||
    fallbackPageMeta(
      context.locale,
      countryBasePage.meta,
      landing.country?.iso || ''
    );
  const originalPageMeta = getTranslation(landing.metas, 'en');

  const heroHeader = getTranslation(landing.heroHeaders, context.locale)
    .text as string;
  const heroHeaderHighlightedText =
    landing.heroHeadersHighlightedText &&
    landing.heroHeadersHighlightedText.length > 0
      ? (getTranslation(landing.heroHeadersHighlightedText, context.locale)
          .text as string)
      : null;
  const heroSubTitle = getTranslation(landing.heroSubTitles, context.locale)
    .text as string;
  const serviceTitle = getTranslation(landing.serviceTitles, context.locale)
    .text as string;
  const serviceText = getTranslation(landing.serviceTexts, context.locale)
    .text as string;
  const body = getTranslation(landing.bodies, context.locale).text as string;
  const alternativeLinks = getAlternateLinks(
    `/declaration-of-use-services/${originalPageMeta.slug}`,
    appRoutes
  );
  const canonicalLink = getCanonicalLink(context.locale || 'en', pageMeta.slug);
  const commonTranslations = await getCommonTranslations();

  const countryRelatedServicesUrlParams = {
    iso: landing.country.iso,
    locale: context.locale,
    apiNamesToExclude: [
      `${entityBaseName}-landing`,
      'copyright-landing',
      'industrial-design-landing',
    ],
  };
  const countryRelatedServices = await fetchStrapi<ICountryRelatedService[]>(
    `/countries/related-services`,
    countryRelatedServicesUrlParams,
    buildApiKey
  );

  const faqsTitle =
    landing.faqsTitle && landing.faqsTitle.length > 0
      ? (getTranslation(landing.faqsTitle, context.locale).text as string)
      : (getTranslation(commonTranslations.faqsTitle, context.locale)
          .text as string);
  const faqs = landing?.faqs
    ?.filter((x) => x.language === context.locale)
    .map((x) => ({
      title: x.question,
      content: x.answer,
    })) as IAccordionListItem[];

  const customerReviewsMeta = (
    await fetchStrapi<any>(`/customer-reviews-meta`, undefined, buildApiKey)
  ).data.attributes as ICustomerReviewsMeta;

  // Check if customer reviews title contains placecholders. Example: "Average rating: {{average}} based on {{numberOfReviews}} reviews (Best rating: {{bestRating}})"
  if (
    customerReviews.title.includes('{{') &&
    customerReviews.title.includes('}}')
  ) {
    customerReviews.title = customerReviews.title.replace(
      '{{average}}',
      customerReviewsMeta.average.toString()
    );
    customerReviews.title = customerReviews.title.replace(
      '{{numberOfReviews}}',
      customerReviewsMeta.numberOfReviews.toString()
    );
    customerReviews.title = customerReviews.title.replace(
      '{{bestRating}}',
      customerReviewsMeta.bestRating.toString()
    );
  }

  // Generate JSON-LD schema
  const currencies = await getAppCurrencies();
  const serviceSchema = generateServiceSchema(
    landing,
    currencies,
    canonicalLink,
    context.locale
  );
  const faqsSchema = generateFaqsSchema(
    faqs.map((x) => ({ question: x.title, answer: x.content }))
  );
  const productSchema = await generateProductSchema(
    heroHeader,
    customerReviewsMeta
  );
  const websiteSchema = generateWebsiteSchema(
    `https://www.igerent.com${
      context.locale !== 'en' ? `/${context.locale}` : ''
    }`,
    getTranslation(commonTranslations.organizationDescription, context.locale)
      .text as string
  );
  const organizationSchema = await generateOrganizationSchema(
    `https://www.igerent.com${
      context.locale !== 'en' ? `/${context.locale}` : ''
    }`,
    getTranslation(commonTranslations.organizationDescription, context.locale)
      .text as string,
    customerReviewsMeta
  );
  const customerReviewsSchema = generateCustomerReviewsSchema(
    reviews,
    customerReviewsMeta
  );

  const pageSchema: PageJSONLDSchema = {
    nodes: [
      ...websiteSchema,
      ...organizationSchema,
      ...serviceSchema,
      ...faqsSchema,
      ...productSchema,
      ...customerReviewsSchema,
    ],
  };

  // Populate breadcrumbs
  if (landing.breadcrumbAncestors && landing.breadcrumbAncestors.length > 0) {
    navBarProps.breadcrumbAncestors = landing.breadcrumbAncestors?.filter(
      (x) => x.language === context.locale
    );
  }

  navBarProps.breadcrumbsHomePageDisplayName = getTranslation(
    commonTranslations.homePageName,
    context.locale
  ).text as string;
  navBarProps.breadcrumbsCurrentPageDisplayName = heroHeader;

  // Prepare Featured Services Call To Action component props
  const featuredServicesCallToAction: ICallToActionComponent = getTranslation(
    commonTranslations.featuredServicesCallToAction,
    context.locale
  );

  const featuredServicesCallToActionProps: ICallToActionProps = {
    title: featuredServicesCallToAction.title,
    subtitle: featuredServicesCallToAction.content,
    buttons: featuredServicesCallToAction.buttons?.map((button) => ({
      label: button.label,
      path: button.path,
    })),
  };

  const formSuccessMessage = isUndefinedOrEmpty(
    landing.contactUsFormSuccessMessage
  )
    ? (getTranslation(
        commonTranslations.contactUsFormSuccessMessage,
        context.locale
      ).text as string)
    : (getTranslation(landing.contactUsFormSuccessMessage, context.locale)
        .text as string);
  const formErrorMessage = isUndefinedOrEmpty(landing.contactUsFormErrorMessage)
    ? (getTranslation(
        commonTranslations.contactUsFormErrorMessage,
        context.locale
      ).text as string)
    : (getTranslation(landing.contactUsFormErrorMessage, context.locale)
        .text as string);

  const howItWorks: ITextblocksWithIconProps = {
    title: getTranslation(landing.howItWorksTitle, context.locale)
      .text as string,
    items: landing
      .howItWorks!.filter((x) => x.language === context.locale)
      .map((x: ITextBlockWithIcon) => ({
        content: x.content,
        icon: flatStrapiObject(x.icon as any) as IMedia,
      })),
  };

  const props: IDeclarationOfUseLandingProps = {
    // Layout
    navBarProps,
    cookiesPopUpProps,
    footerProps,
    // Page
    locale: context.locale,
    meta: pageMeta,
    heroHeader,
    heroHeaderHighlightedText,
    heroSubTitle,
    serviceTitle,
    serviceText,
    body,
    heroTipsTitle: getTranslation(countryBasePage.heroTipsTitle, context.locale)
      .text as string,
    heroTipOne: getTranslation(countryBasePage.heroTipOne, context.locale)
      .text as string,
    heroTipTwo: getTranslation(countryBasePage.heroTipTwo, context.locale)
      .text as string,
    heroTipThree: getTranslation(countryBasePage.heroTipThree, context.locale)
      .text as string,
    businessKeyValues,
    trustedBy,
    customerReviews,
    reviews,
    fromLabel: getTranslation(countryBasePage.fromLabel, context.locale)
      .text as string,
    orderNowLabel: getTranslation(countryBasePage.orderNowLabel, context.locale)
      .text as string,
    contactMethods,
    formSuccessMessage,
    formErrorMessage,
    contactUsForm,
    countryIso: landing.country.iso,
    fromPrice: landing.fromPrice,
    alternativeLinks,
    canonicalLink,
    relatedServicesTitle: getTranslation(
      countryBasePage.relatedServicesTitle,
      context.locale
    ).text as string,
    relatedServices: countryRelatedServices.map((x) => ({
      title: x.title,
      link: getLocalizedAppRoute(x.slug),
    })),
    feesIncludedLabel: getTranslation(
      commonTranslations.feesIncluded,
      context.locale
    ).text as string,
    faqsTitle,
    faqs,
    pageSchema,
    featuredServicesCallToActionProps,
    customerReviewsMeta,
    customerReviewsTopTipStart: getTranslation(
      commonTranslations.customerReviewsTopTipStart,
      context.locale
    ).text as string,
    customerReviewsTopTipEnd: getTranslation(
      commonTranslations.customerReviewsTopTipEnd,
      context.locale
    ).text as string,
    customerReviewsBottomTip: getTranslation(
      commonTranslations.customerReviewsBottomTip,
      context.locale
    ).text as string,
    howItWorks,
    introductionTitle:
      getTranslation(landing.introductionTitle, context.locale).text || '-',
    introductionDescription:
      getTranslation(landing.introductionDescription, context.locale).text ||
      '-',
    introductionImage: flatStrapiObject(landing.introductionImage),
  };

  return {
    props,
  };
}

export default DeclarationOfUseLandingPage;
